import {
  DEFAULT_API_REGION_SLUG,
  parseApiGatewayRegionSlugFromQuery,
  setApiRegionSlug,
} from "../helper/api-region-slug";
import { resolveBaseDomain, isPortalGoreloIoHost } from "../helper/public-function-access";
import { Enums } from "../helper/enums";
import { resolveBootEmail } from "../helper/resolve-boot-email";
import { UrlHelper } from "../helper/url-helper-service";
import { authService } from "./auth-service";

/** Avoid duplicate contact KV fetches when bootstrap runs more than once per session. */
let lastContactLookupEmail: string | null = null;

/** KV lookup `region` / `Region`: `"2"` → au; anything else (including missing) → usw. */
function toApiGatewayRegionSlug(
  regionCode: string | number | null | undefined,
): Enums.ApiGatewayRegionSlug {
  if (String(regionCode).trim() === Enums.ApiGatewayRegionClaim.Australia) {
    return Enums.ApiGatewayRegionSlug.Australia;
  }

  return DEFAULT_API_REGION_SLUG;
}

function getRegionCodeFromLookupResponse(
  lookupResponse: unknown,
): string | number | null | undefined {
  if (!lookupResponse || typeof lookupResponse !== "object") {
    return undefined;
  }

  const response = lookupResponse as Record<string, unknown>;
  return (response.region ?? response.Region) as
    | string
    | number
    | null
    | undefined;
}

function getContactRegionLookupBaseUrl(): string {
  return (import.meta.env.VITE_regionLookupUrl || "").replace(/\/$/, "");
}

function getDomainRegionLookupBaseUrl(): string {
  return (import.meta.env.VITE_domainLookupUrl || "").replace(/\/$/, "");
}

export function buildRegionLookupUrlForEmail(email: string): string {
  const query = new URLSearchParams({
    email: email.trim(),
    isContact: "true",
  });
  return `${getContactRegionLookupBaseUrl()}?${query.toString()}`;
}

export function buildDomainLookupUrl(domain: string): string {
  const query = new URLSearchParams({
    domain: domain.trim(),
  });
  return `${getDomainRegionLookupBaseUrl()}?${query.toString()}`;
}

async function fetchRegionSlugFromLookupUrl(
  lookupUrl: string,
): Promise<Enums.ApiGatewayRegionSlug> {
  if (!lookupUrl.startsWith("http")) {
    return DEFAULT_API_REGION_SLUG;
  }

  try {
    const response = await fetch(lookupUrl);
    if (!response.ok) {
      return DEFAULT_API_REGION_SLUG;
    }
    const lookupResponse: unknown = await response.json();
    const regionCode = getRegionCodeFromLookupResponse(lookupResponse);
    return toApiGatewayRegionSlug(regionCode);
  } catch {
    return DEFAULT_API_REGION_SLUG;
  }
}

export async function fetchRegionSlugForEmail(
  email: string,
): Promise<Enums.ApiGatewayRegionSlug> {
  if (!email.trim()) {
    return DEFAULT_API_REGION_SLUG;
  }
  return fetchRegionSlugFromLookupUrl(buildRegionLookupUrlForEmail(email));
}

export async function fetchRegionSlugForDomain(
  domain: string,
): Promise<Enums.ApiGatewayRegionSlug> {
  if (!domain.trim()) {
    return DEFAULT_API_REGION_SLUG;
  }
  return fetchRegionSlugFromLookupUrl(buildDomainLookupUrl(domain));
}

function resolveContactLookupEmail(): string | null {
  return (
    resolveBootEmail() ??
    authService.GetAuthenticatedUserEmail()?.trim() ??
    null
  );
}

function resolveRegionQueryFromUrl(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const readRegion = (params: URLSearchParams): string | null => {
    const region = params.get("region")?.trim();
    return region || null;
  };

  const hash = window.location.hash;
  if (hash.length > 1) {
    const hashContent = hash.slice(1);
    const queryStart = hashContent.indexOf("?");
    if (queryStart !== -1) {
      let queryPart = hashContent.slice(queryStart + 1);
      const fragmentInQuery = queryPart.indexOf("#");
      if (fragmentInQuery !== -1) {
        queryPart = queryPart.slice(0, fragmentInQuery);
      }
      const region = readRegion(new URLSearchParams(queryPart));
      if (region) {
        return region;
      }
    }
  }

  if (window.location.search) {
    const region = readRegion(new URLSearchParams(window.location.search));
    if (region) {
      return region;
    }
  }

  return null;
}

function resolveMagicLinkVerifyRegionFromUrl(): string | null {
  return resolveRegionQueryFromUrl();
}

/** Magic-link verify — region from `?region=` query param (no KV). */
export function applyRegionFromMagicLinkQuery(
  region: string | null | undefined,
): void {
  setApiRegionSlug(parseApiGatewayRegionSlugFromQuery(region));
}

/** Same as {@link applyRegionFromMagicLinkQuery} but reads `region` from the current URL. */
export function applyRegionFromMagicLinkVerifyUrl(): void {
  applyRegionFromMagicLinkQuery(resolveMagicLinkVerifyRegionFromUrl());
}

/** portal.gorelo.io public doc links — region from `?region=` query param (no KV). */
export function applyRegionFromPublicDocumentUrl(): void {
  applyRegionFromMagicLinkQuery(resolveRegionQueryFromUrl());
}

/** Magic-link request — region from contact-email KV before regional APIM call. */
export async function applyRegionFromContactKvForEmail(
  email: string,
): Promise<void> {
  const regionSlug = await fetchRegionSlugForEmail(email);
  setApiRegionSlug(regionSlug);
}

/** Custom domains — region from domain KV (current hostname). */
export async function bootstrapRegionFromDomainKv(): Promise<void> {
  try {
    const domain = resolveBaseDomain();
    const regionSlug = domain
      ? await fetchRegionSlugForDomain(domain)
      : DEFAULT_API_REGION_SLUG;
    setApiRegionSlug(regionSlug);
  } catch {
    setApiRegionSlug(DEFAULT_API_REGION_SLUG);
  }
}

/** portal.gorelo.io — region from contact-email KV. No-op when no email is available yet. */
export async function bootstrapRegionFromContactKv(): Promise<void> {
  try {
    const email = resolveContactLookupEmail();
    if (!email || email === lastContactLookupEmail) {
      return;
    }
    const regionSlug = await fetchRegionSlugForEmail(email);
    setApiRegionSlug(regionSlug);
    lastContactLookupEmail = email;
  } catch {
    setApiRegionSlug(DEFAULT_API_REGION_SLUG);
  }
}

/** App boot — resolve API region from the current URL. */
export async function bootstrapRegionFromUrl(): Promise<void> {
  if (UrlHelper.isAuthVerificationURL()) {
    applyRegionFromMagicLinkVerifyUrl();
  } else if (UrlHelper.isPublicURL() && isPortalGoreloIoHost()) {    
      applyRegionFromPublicDocumentUrl();    
  } else if (isPortalGoreloIoHost() && !UrlHelper.isPublicURL()) {
    await bootstrapRegionFromContactKv();
  } else {
    await bootstrapRegionFromDomainKv();
  }
}
